import {announce} from '@github-ui/aria-live'
import {createEstablishedAhpPendingMessage} from '@github-ui/ahp-session-client/contracts'
import {
  clientInteractionAccepted,
  clientInteractionFailed,
  clientInteractionForQueuedMessage,
} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {useQueuedClientInteractions} from '@github-ui/copilot-browser-latency/use-copilot-client-interactions'
import type {PendingMessage} from '@microsoft/agent-host-protocol'
import {clsx} from 'clsx'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref} from 'react'

import {
  QueuedMessageControl,
  type QueuedMessageControlItem,
  type QueuedMessageControlItemId,
} from './QueuedMessageControl'
import styles from './QuickChatQueuedMessages.module.css'
import {useOptionalEstablishedAhpSession} from '../hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '../hooks/use-established-ahp-session-view'

const MAX_QUEUED_MESSAGE_LABEL_LENGTH = 80
const POSSIBLE_DUPLICATE_WARNING =
  'The message was sent now, but could not be removed from the queue. It may be delivered twice.'

export interface QueuedMessageActionResult {
  readonly possibleDuplicate?: boolean
}

type QueuedMessageAction = (
  message: PendingMessage,
) => void | QueuedMessageActionResult | Promise<void | QueuedMessageActionResult>

function summarizeQueuedMessageText(text: string | undefined): string {
  const normalized = text?.replace(/\s+/g, ' ').trim()
  if (!normalized) return 'message with attachments'
  if (normalized.length <= MAX_QUEUED_MESSAGE_LABEL_LENGTH) return normalized
  return `${normalized.slice(0, MAX_QUEUED_MESSAGE_LABEL_LENGTH - 1)}…`
}

function queuedMessageContent(text: string | undefined): ReactNode {
  return text && text.length > 0 ? text : 'Message with attachments'
}

function addId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
  return ids.has(id) ? ids : new Set(ids).add(id)
}

function removeId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!ids.has(id)) return ids
  const next = new Set(ids)
  next.delete(id)
  return next
}

function errorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback
  const prefix = fallback.endsWith('.') ? fallback.slice(0, -1) : fallback
  return `${prefix}: ${error.message}`
}

export interface QuickChatQueuedMessagesProps {
  /** Messages queued to run automatically as new turns once the current turn finishes. */
  queuedMessages?: readonly PendingMessage[]
  /** Called with the opaque queued message id to remove it from the queue. */
  onRemoveQueued?: (id: string) => void | Promise<void>
  /**
   * Called with the full queued message to send it immediately. The callback owns the complete
   * operation, including removal from the authoritative queue.
   */
  onSendNowQueued?: QueuedMessageAction
  /** Marks the queue read-only, hiding remove/send-now actions. */
  readOnly?: boolean
  /** Invoked when focus cannot be restored to another queue control. */
  onFocusFallback?: () => void
  onExpandedChange?: (expanded: boolean) => void
  onRemoveStarted?: (id: string) => void
  onSendNowStarted?: (message: PendingMessage) => void
  renderQueuedMessage?: (message: PendingMessage) => ReactNode
  queuedMessagesAriaLabel?: string
  fillAvailableSpace?: boolean
  className?: string
  ref?: Ref<HTMLDivElement>
  /** The active composer, confirmation, or ask-user prompt rendered below the queue. */
  children?: ReactNode
}

/**
 * Shared queue and composer surface used by Quick Chat and Mission Control.
 * The queue stays mounted while the child surface changes between normal input,
 * confirmation, and ask-user states.
 */
export function QuickChatQueuedMessages({
  queuedMessages,
  onRemoveQueued,
  onSendNowQueued,
  readOnly = false,
  onFocusFallback,
  onExpandedChange,
  onRemoveStarted,
  onSendNowStarted,
  renderQueuedMessage,
  queuedMessagesAriaLabel = 'Queued messages',
  fillAvailableSpace = false,
  className,
  ref,
  children,
}: QuickChatQueuedMessagesProps) {
  const establishedSession = useOptionalEstablishedAhpSession()
  const establishedView = useOptionalEstablishedAhpSessionViewState()
  const resolvedQueuedMessages = queuedMessages ?? establishedView.state?.queuedMessages
  const removeQueued = useMemo(
    () =>
      onRemoveQueued ?? (establishedSession ? (id: string) => establishedSession.input.removeQueued(id) : undefined),
    [establishedSession, onRemoveQueued],
  )
  const sendNowQueued = useMemo(
    () =>
      onSendNowQueued ??
      (establishedSession
        ? async (message: PendingMessage) => {
            await establishedSession.input.sendNow(createEstablishedAhpPendingMessage(message))
          }
        : undefined),
    [establishedSession, onSendNowQueued],
  )
  const resolvedReadOnly = readOnly || establishedView.passive
  const observeSendNow = useQueuedClientInteractions({
    role: resolvedReadOnly ? 'read_only' : 'writable',
    session_id: establishedSession?.identity?.sessionId,
    environment_id: establishedSession?.identity?.environmentId,
    task_id: establishedSession?.identity?.taskId,
    chat_id: establishedView.state?.resource,
  })
  const handleSendNowIntent = useCallback(
    (id: QueuedMessageControlItemId, event: Event, button: HTMLButtonElement) => {
      const message = resolvedQueuedMessages?.find(candidate => candidate.id === String(id))
      if (message) observeSendNow?.(message, event, button)
    },
    [observeSendNow, resolvedQueuedMessages],
  )

  const queuedMessagesByIdRef = useRef<Map<string, string> | undefined>(undefined)
  const removalRequestedIdsRef = useRef(new Set<string>())
  const sendNowRemovalRequestedIdsRef = useRef(new Set<string>())
  const [removingIds, setRemovingIds] = useState<ReadonlySet<string>>(() => new Set())
  const [sendingIds, setSendingIds] = useState<ReadonlySet<string>>(() => new Set())
  const [possibleDuplicateIds, setPossibleDuplicateIds] = useState<ReadonlySet<string>>(() => new Set())

  const queuedIds = new Set(resolvedQueuedMessages?.map(message => message.id) ?? [])
  const staleRemovingIds = Array.from(removingIds).filter(id => !queuedIds.has(id))
  const staleSendingIds = Array.from(sendingIds).filter(id => !queuedIds.has(id))
  const stalePossibleDuplicateIds = Array.from(possibleDuplicateIds).filter(id => !queuedIds.has(id))

  if (staleRemovingIds.length > 0) {
    setRemovingIds(previousIds => {
      let nextIds = previousIds
      for (const id of staleRemovingIds) nextIds = removeId(nextIds, id)
      return nextIds
    })
  }
  if (staleSendingIds.length > 0) {
    setSendingIds(previousIds => {
      let nextIds = previousIds
      for (const id of staleSendingIds) nextIds = removeId(nextIds, id)
      return nextIds
    })
  }
  if (stalePossibleDuplicateIds.length > 0) {
    setPossibleDuplicateIds(previousIds => {
      let nextIds = previousIds
      for (const id of stalePossibleDuplicateIds) nextIds = removeId(nextIds, id)
      return nextIds
    })
  }

  useEffect(() => {
    const nextQueuedMessagesById = new Map(
      (resolvedQueuedMessages ?? []).map(message => [message.id, summarizeQueuedMessageText(message.message.text)]),
    )
    const previousQueuedMessagesById = queuedMessagesByIdRef.current
    queuedMessagesByIdRef.current = nextQueuedMessagesById
    if (!previousQueuedMessagesById) return

    const addedCount = Array.from(nextQueuedMessagesById.keys()).filter(
      id => !previousQueuedMessagesById.has(id),
    ).length
    if (addedCount > 0) {
      announce(
        addedCount === 1
          ? 'Message queued. It will be sent after the current response finishes.'
          : `${addedCount} messages queued. They will be sent after the current response finishes.`,
      )
    }

    const removedMessages = Array.from(previousQueuedMessagesById.entries()).filter(
      ([id]) => !nextQueuedMessagesById.has(id),
    )
    const removedMessageSummaries = removedMessages
      .filter(([id]) => removalRequestedIdsRef.current.has(id))
      .map(([, summary]) => summary)
    const sentNowCount = removedMessages.filter(([id]) => sendNowRemovalRequestedIdsRef.current.has(id)).length

    if (removedMessageSummaries.length > 0) {
      announce(
        removedMessageSummaries.length === 1
          ? `Queued message removed: ${removedMessageSummaries[0]}.`
          : `${removedMessageSummaries.length} queued messages removed.`,
      )
    }
    if (sentNowCount > 0) {
      announce(sentNowCount === 1 ? 'Queued message sent now.' : `${sentNowCount} queued messages sent now.`)
    }
    for (const [id] of removedMessages) {
      removalRequestedIdsRef.current.delete(id)
      sendNowRemovalRequestedIdsRef.current.delete(id)
    }
  }, [onFocusFallback, resolvedQueuedMessages])

  const items: QueuedMessageControlItem[] = useMemo(
    () =>
      (resolvedQueuedMessages ?? []).map(message => ({
        id: message.id,
        content: renderQueuedMessage?.(message) ?? queuedMessageContent(message.message.text),
        accessibleLabel: summarizeQueuedMessageText(message.message.text),
        removePending: removingIds.has(message.id),
        removeDisabled: sendingIds.has(message.id),
        sendNowPending: sendingIds.has(message.id),
        sendNowDisabled: removingIds.has(message.id) || possibleDuplicateIds.has(message.id),
      })),
    [possibleDuplicateIds, removingIds, renderQueuedMessage, resolvedQueuedMessages, sendingIds],
  )

  const handleRemove = useCallback(
    (id: QueuedMessageControlItemId) => {
      const stringId = String(id)
      const isLastQueuedMessage = resolvedQueuedMessages?.length === 1
      if (!removeQueued) return
      onRemoveStarted?.(stringId)
      removalRequestedIdsRef.current.add(stringId)
      setRemovingIds(previousIds => addId(previousIds, stringId))
      void (async () => {
        try {
          await removeQueued(stringId)
        } catch (error) {
          removalRequestedIdsRef.current.delete(stringId)
          setRemovingIds(previousIds => removeId(previousIds, stringId))
          announce(errorMessage(error, 'Queued message could not be removed.'), {assertive: true})
          return
        }
        if (isLastQueuedMessage) {
          requestAnimationFrame(() => requestAnimationFrame(() => onFocusFallback?.()))
        }
      })()
    },
    [onFocusFallback, onRemoveStarted, removeQueued, resolvedQueuedMessages],
  )

  const handleSendNow = useCallback(
    (id: QueuedMessageControlItemId) => {
      const stringId = String(id)
      const message = resolvedQueuedMessages?.find(candidate => candidate.id === stringId)
      const isLastQueuedMessage = resolvedQueuedMessages?.length === 1
      if (!message || !sendNowQueued) return
      const interaction = clientInteractionForQueuedMessage(message)
      onSendNowStarted?.(message)
      setSendingIds(previousIds => addId(previousIds, stringId))
      void (async () => {
        try {
          const result = await sendNowQueued(message)
          if (result?.possibleDuplicate) {
            clientInteractionFailed(interaction)
            setSendingIds(previousIds => removeId(previousIds, stringId))
            setPossibleDuplicateIds(previousIds => addId(previousIds, stringId))
            announce(POSSIBLE_DUPLICATE_WARNING, {assertive: true})
            return
          }
          sendNowRemovalRequestedIdsRef.current.add(stringId)
          clientInteractionAccepted(interaction)
          if (isLastQueuedMessage) {
            requestAnimationFrame(() => requestAnimationFrame(() => onFocusFallback?.()))
          }
        } catch (error) {
          clientInteractionFailed(interaction)
          setSendingIds(previousIds => removeId(previousIds, stringId))
          announce(errorMessage(error, 'Queued message could not be sent now.'), {assertive: true})
        }
      })()
    },
    [onFocusFallback, onSendNowStarted, resolvedQueuedMessages, sendNowQueued],
  )

  const hasQueuedMessages = items.length > 0

  return (
    <div
      ref={ref}
      className={clsx(styles.ComposerSurface, className)}
      data-fill-available-space={fillAvailableSpace ? 'true' : undefined}
      data-testid={hasQueuedMessages ? 'quick-chat-queued-messages' : undefined}
    >
      <div className={hasQueuedMessages ? styles.QueuedMessagesRegion : undefined}>
        <QueuedMessageControl
          items={items}
          readOnly={resolvedReadOnly}
          ariaLabel={queuedMessagesAriaLabel}
          onExpandedChange={onExpandedChange}
          onRemove={removeQueued ? handleRemove : undefined}
          onSendNow={sendNowQueued ? handleSendNow : undefined}
          onSendNowIntent={observeSendNow ? handleSendNowIntent : undefined}
          onFocusFallback={onFocusFallback}
        />
      </div>
      {possibleDuplicateIds.size > 0 ? (
        <Flash className={styles.QueuedMessagesWarning} variant="warning">
          {POSSIBLE_DUPLICATE_WARNING}
        </Flash>
      ) : null}
      <div className={styles.ComposerRegion}>{children}</div>
    </div>
  )
}

QuickChatQueuedMessages.displayName = 'QuickChatQueuedMessages'
